// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import X from "@insite/mobius/Icons/X";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getNotifyStyle } from "@sc/App/Components/Notify";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import productDescriptionVariantStyles from "@sc/App/SharedStyles/ProductDescriptionStyles";
import xProductPartNumbersStyles from "@sc/App/SharedStyles/ProductPartNumbersStyles";
import { ProductPriceStyles } from "@sc/Overrides/Components/ProductPrice";
import AddToList from "@sc/XDL/Icons/AddToList";
import Minus from "@sc/XDL/Icons/Minus";
import Plus from "@sc/XDL/Icons/Plus";
import Remove from "@sc/XDL/Icons/Remove";
import { color, createLinkIconStyles, ellipsis, mergeWithCss, xScreen } from "@sc/XDL/Utilities";
import { css } from "styled-components";

export interface CartLineSharedStyles {
    productImage?: ProductImageStyles;
    productDescription?: ProductDescriptionStyles;
    productPartNumbers?: ProductPartNumbersStyles;
    addToListLink?: LinkPresentationProps;
    hideLineNoteLink?: LinkPresentationProps;
    showLineNoteLink?: LinkPresentationProps;
    quantityHeadingAndText?: SmallHeadingAndTextStyles;
    quantityTextField?: TextFieldPresentationProps;
    qtyLabel?: TypographyProps;
    productPrice?: ProductPriceStyles;
    extendedUnitNetPriceText?: SmallHeadingAndTextStyles;
    costCodeGridItem?: GridItemProps;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeValueText?: TypographyPresentationProps;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    promotionsContainer?: InjectableCss;
    promotionNameLabel?: TypographyPresentationProps;
    promotionNameText?: InjectableCss;
    removeCartLineIcon?: IconPresentationProps;
    cartLineNotes?: CartLineNotesStyles;
    cartLineErrorMessageGridItem?: GridItemProps;
    errorMessage?: TypographyProps;
    productAvailability?: ProductAvailabilityStyles;
    configurationOptionText?: TypographyProps;
    configurationOptionLabel?: TypographyProps;
    unitOfMeasure?: TypographyProps;
    badgesWrapper?: InjectableCss;
    extendedUnitNetPriceHeadingAndTextWrapper?: InjectableCss;
    notify?: TypographyProps;
}

const cartLineSharedStyles: CartLineSharedStyles = {
    productImage: {
        linkWrappingImage: {
            css: css`
                width: 91px;
                height: 91px;
                background-color: ${color("common.white")};
                justify-content: center;
                &&& > img {
                    max-width: 100%;
                    max-height: 100%;
                    height: auto;
                    object-fit: contain;
                }
            `,
        },
    },
    productDescription: {
        productDetailLink: {
            ...mergeToNew(productDescriptionVariantStyles?.medium?.productDetailLink, {
                typographyProps: {
                    css: css`
                        ${xScreen("lte", "md")} {
                            font-size: 14px;
                            line-height: 16px;
                        }
                    `,
                },
            }),
        },
        productDetailDescription: {
            ...mergeToNew(productDescriptionVariantStyles?.medium?.productDetailDescription, {
                css: css`
                    ${xScreen("lte", "md")} {
                        font-size: 14px;
                        line-height: 16px;
                    }
                `,
            }),
        },
    },
    productPartNumbers: {},
    addToListLink: {
        ...createLinkIconStyles(AddToList),
    },
    hideLineNoteLink: {
        ...createLinkIconStyles(Minus, {
            icon: {
                iconProps: {
                    size: 11,
                },
            },
        }),
    },
    showLineNoteLink: {
        ...createLinkIconStyles(Plus, {
            icon: {
                iconProps: {
                    size: 11,
                },
            },
        }),
    },
    productPrice: {
        wrapper: {
            css: css`
                flex-direction: column;
                align-items: baseline;
                margin-bottom: 7px;
                ${xScreen("lte", "md")} {
                    display: block;
                }
            `,
        },
        failedToLoadPricingWrapper: {
            css: css`
                margin-bottom: 8px;
            `,
        },
        priceLabelText: {
            size: 12,
            css: css`
                display: none;
                ${xScreen("lte", "md")} {
                    display: block;
                }
                @media print {
                    font-size: 14px;
                    line-height: 16px;
                    margin-bottom: 4px;
                }
            `,
        },
        price: {
            priceText: {
                size: 16,
                lineHeight: "22px",
                css: css`
                    ${xScreen("lte", "md")} {
                        font-size: 14px;
                        line-height: 19px;
                    }
                `,
            },
        },
        quoteMessage: {
            text: {
                css: css`
                    ${xScreen("lte", "md")} {
                        font-size: 14px;
                        line-height: 19px;
                    }
                `,
            },
        },
    },
    extendedUnitNetPriceHeadingAndTextWrapper: {
        css: css`
            ${xScreen("lte", "md")} {
                height: 100%;
            }
        `,
    },
    extendedUnitNetPriceText: {
        extendedStyles: {
            heading: {
                size: 12,
                weight: "normal",
                css: css`
                    line-height: 14px;
                    text-transform: capitalize;
                    padding: 0;
                    margin-bottom: 4px;
                    display: none;
                    ${xScreen("lte", "md")} {
                        display: block;
                        margin-bottom: 4px;
                    }
                    @media print {
                        &&& {
                            display: block;
                            font-size: 14px;
                            line-height: 16px;
                        }
                    }
                `,
            },
            text: {
                size: 16,
                weight: "bold",
                lineHeight: "22px",
                css: css`
                    text-align: start;
                    display: flex;
                    ${xScreen("lte", "md")} {
                        font-size: 14px;
                        line-height: 19px;
                    }
                `,
            },
            wrapper: {
                css: css`
                    height: 100%;
                    justify-content: space-between;
                `,
            },
        },
    },
    costCodeGridItem: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 10px;
        `,
    },
    costCodeLabelText: {
        ...xProductPartNumbersStyles.label,
    },
    costCodeValueText: {
        ...xProductPartNumbersStyles.value,
    },
    configurationOptionText: {
        ...xProductPartNumbersStyles.value,
    },
    configurationOptionLabel: {
        ...xProductPartNumbersStyles.label,
    },
    promotionsContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 10px;
        `,
    },
    promotionNameLabel: {
        ...xProductPartNumbersStyles.label,
    },
    promotionNameText: {
        ...xProductPartNumbersStyles.value,
    },
    qtyLabel: {
        as: "label",
        size: 12,
        weight: "normal",
        lineHeight: "17px",
        css: css`
            margin-bottom: 4px;
            display: none;
            ${xScreen("lte", "md")} {
                display: block;
            }
            @media print {
                &&& {
                    font-size: 14px;
                    line-height: 16px;
                }
            }
        `,
    },
    removeCartLineIcon: {
        src: Remove,
        size: 16,
        color: "primary",
        css: css`
            &:hover {
                color: ${color("common.ui8")};
            }
            ${xScreen("lte", "md")} {
                width: 13px;
                height: 13px;
                svg {
                    width: 13px;
                    height: 13px;
                }
            }
        `,
    },
    cartLineNotes: {
        wrapper: {
            css: css`
                margin-top: 4px;
                ${xScreen("lte", "md")} {
                    width: 100%;
                }
            `,
        },
        editableNotesTextArea: {
            css: css`
                &&&& {
                    overflow: auto;
                }
            `,
        },
    },
    cartLineErrorMessageGridItem: {
        css: css`
            display: flex;
            flex-direction: column;
            ${xScreen("lte", "md")} {
                margin-top: 10px;
            }
        `,
    },
    errorMessage: {
        ...mergeWithCss(getNotifyStyle("danger"), {
            css: css`
                min-width: initial;
            `,
        }),
    },
    productAvailability: {
        messageGridItem: {
            css: css`
                & > div {
                    margin-bottom: 10px;
                }
            `,
        },
    },
    unitOfMeasure: {
        size: 12,
        weight: "normal",
        css: css`
            line-height: 13px;
            text-transform: capitalize;
        `,
    },
    badgesWrapper: {
        css: css`
            display: flex;
            gap: 8px;
            margin-bottom: 8px;
            &:empty {
                display: none;
            }
        `,
    },
};

export default cartLineSharedStyles;
